import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowDownToLine, ArrowUpFromLine, Users, Copy, Check, X, Plus, RefreshCw, ShieldCheck, AlertTriangle, Megaphone, Flame } from 'lucide-react';
import BannersPanel from '@/components/BannersPanel';
import BgnStakingAdminPanel from '@/components/BgnStakingAdminPanel';
import StakingPoolsAdminPanel from '@/components/StakingPoolsAdminPanel';
import ListingsAdminPanel from '@/components/ListingsAdminPanel';
import HDWalletAdminPanel from '@/components/HDWalletAdminPanel';

const NETWORKS = {
  BTC: ['BTC'],
  ETH: ['ERC20'],
  USDT: ['ERC20', 'TRC20', 'BEP20'],
  BNB: ['BEP20'],
  SOL: ['SOL'],
};

// Full admin "Ops Queue" panel — address pool + user assignments + deposit
// approval + withdrawal approval + KYC review. Rendered as a sub-tab within
// AdminDashboard when tab === 'ops'.
export default function AdminOpsPanel() {
  const [sub, setSub] = useState('pool');
  const [pool, setPool] = useState([]);
  const [poolCounts, setPoolCounts] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [depReqs, setDepReqs] = useState([]);
  const [wdReqs, setWdReqs] = useState([]);
  const [kycQueue, setKycQueue] = useState([]);
  const [kycThreshold, setKycThreshold] = useState(50000);
  const [kycThresholdDraft, setKycThresholdDraft] = useState('');
  const [tier2Threshold, setTier2Threshold] = useState(500000);
  const [tier2ThresholdDraft, setTier2ThresholdDraft] = useState('');
  const [kycSaving, setKycSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // Modal state — one dialog for all approve/reject actions
  const [action, setAction] = useState(null); // { kind, row, mode: 'approve'|'reject'|'retire' }
  const [actionField1, setActionField1] = useState('');
  const [actionField2, setActionField2] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  // Add pool address form
  const [newSym, setNewSym] = useState('USDT');
  const [newNet, setNewNet] = useState('TRC20');
  const [newAddr, setNewAddr] = useState('');
  const [newMemo, setNewMemo] = useState('');
  const [newNotes, setNewNotes] = useState('');
  // Assign address to user
  const [assignUserId, setAssignUserId] = useState('');
  const [assignSym, setAssignSym] = useState('USDT');
  const [assignNet, setAssignNet] = useState('TRC20');
  const [assignAddr, setAssignAddr] = useState('');
  const [assignMemo, setAssignMemo] = useState('');

  const loadPool = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/deposit_addresses?limit=500');
      setPool(r.data.addresses || []); setPoolCounts(r.data.counts || {});
    } catch (e) { toast.error(_err(e, 'Failed to load pool')); }
    finally { setLoading(false); }
  };
  const loadAssignments = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/user_addresses?limit=500');
      setAssignments(r.data.assignments || []);
    } catch (e) { toast.error(_err(e, 'Failed to load assignments')); }
    finally { setLoading(false); }
  };
  const loadDeposits = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/deposits/pending?status=pending&limit=200');
      setDepReqs(r.data.requests || []);
    } catch (e) { toast.error(_err(e, 'Failed to load deposits')); }
    finally { setLoading(false); }
  };
  const loadWithdraws = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/withdrawals/pending?status=pending&limit=200');
      setWdReqs(r.data.requests || []);
    } catch (e) { toast.error(_err(e, 'Failed to load withdrawals')); }
    finally { setLoading(false); }
  };
  const loadKyc = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/kyc/queue?status=pending&limit=200');
      setKycQueue(r.data.submissions || []);
    } catch (e) { toast.error(_err(e, 'Failed to load KYC queue')); }
    finally { setLoading(false); }
  };
  const loadSettings = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/config/kyc_threshold');
      setKycThreshold(r.data.threshold_usd);
      setKycThresholdDraft(String(r.data.threshold_usd));
      setTier2Threshold(r.data.tier2_threshold_usd);
      setTier2ThresholdDraft(String(r.data.tier2_threshold_usd));
    } catch (e) { toast.error(_err(e, 'Failed to load settings')); }
    finally { setLoading(false); }
  };
  const saveKycThreshold = async () => {
    const v = parseFloat(kycThresholdDraft);
    if (isNaN(v) || v < 0) { toast.error('Enter a valid positive number'); return; }
    setKycSaving(true);
    try {
      await api.post('/admin/config/kyc_threshold', { threshold_usd: v });
      toast.success(`Tier 1 cap updated to $${v.toLocaleString()}`);
      setKycThreshold(v);
    } catch (e) { toast.error(_err(e, 'Failed to save')); }
    finally { setKycSaving(false); }
  };
  const saveTier2Threshold = async () => {
    const v = parseFloat(tier2ThresholdDraft);
    if (isNaN(v) || v < 0) { toast.error('Enter a valid positive number'); return; }
    setKycSaving(true);
    try {
      await api.post('/admin/config/kyc_threshold', { tier2_threshold_usd: v });
      toast.success(`Tier 2 cap updated to $${v.toLocaleString()}`);
      setTier2Threshold(v);
    } catch (e) { toast.error(_err(e, 'Failed to save')); }
    finally { setKycSaving(false); }
  };
  useEffect(() => {
    if (sub === 'pool') loadPool();
    else if (sub === 'assignments') loadAssignments();
    else if (sub === 'deposits') loadDeposits();
    else if (sub === 'withdrawals') loadWithdraws();
    else if (sub === 'kyc') loadKyc();
    else if (sub === 'settings') loadSettings();
  }, [sub]);

  const openAction = (kind, mode, row) => {
    setAction({ kind, mode, row });
    // Prefill sensible defaults
    if (kind === 'deposit' && mode === 'approve') { setActionField1(String(row.amount)); setActionField2(''); }
    else if (kind === 'deposit' && mode === 'reject') { setActionField1('Tx hash could not be verified'); setActionField2(''); }
    else if (kind === 'withdraw' && mode === 'approve') { setActionField1(''); setActionField2(''); }
    else if (kind === 'withdraw' && mode === 'reject') { setActionField1('Suspicious activity'); setActionField2(''); }
    else if (kind === 'kyc' && mode === 'reject') { setActionField1('Documents unclear'); setActionField2(''); }
    else { setActionField1(''); setActionField2(''); }
  };
  const closeAction = () => { setAction(null); setActionField1(''); setActionField2(''); setActionBusy(false); };

  const runAction = async () => {
    if (!action) return;
    setActionBusy(true);
    try {
      const { kind, mode, row } = action;
      if (kind === 'deposit' && mode === 'approve') {
        const amt = parseFloat(actionField1);
        if (!amt || amt <= 0) throw new Error('Credit amount must be > 0');
        await api.post(`/admin/deposits/${row.request_id}/approve`, { credit_amount: amt, note: actionField2 || null });
        toast.success(`Credited ${amt} ${row.symbol}`);
        loadDeposits();
      } else if (kind === 'deposit' && mode === 'reject') {
        if (!actionField1.trim()) throw new Error('Rejection reason required');
        await api.post(`/admin/deposits/${row.request_id}/reject`, { note: actionField1.trim() });
        toast.success('Deposit rejected');
        loadDeposits();
      } else if (kind === 'withdraw' && mode === 'approve') {
        const r = await api.post(`/admin/withdrawals/${row.request_id}/approve`, {
          tx_hash: actionField1.trim() || null,
          note: actionField2.trim() || null,
        });
        toast.success(`Approved — tx ${r.data.tx_hash?.slice(0, 12)}…`);
        loadWithdraws();
      } else if (kind === 'withdraw' && mode === 'reject') {
        if (!actionField1.trim()) throw new Error('Rejection reason required');
        await api.post(`/admin/withdrawals/${row.request_id}/reject`, { note: actionField1.trim() });
        toast.success(`Rejected — refunded ${row.amount} ${row.symbol}`);
        loadWithdraws();
      } else if (kind === 'kyc' && mode === 'approve') {
        await api.post(`/admin/kyc/${row.kyc_id}/approve`);
        toast.success('KYC approved');
        loadKyc();
      } else if (kind === 'kyc' && mode === 'reject') {
        if (!actionField1.trim()) throw new Error('Rejection reason required');
        await api.post(`/admin/kyc/${row.kyc_id}/reject`, { reason: actionField1.trim() });
        toast.success('KYC rejected');
        loadKyc();
      } else if (kind === 'pool' && mode === 'retire') {
        await api.delete(`/admin/deposit_addresses/${row.address_id}`);
        toast.success('Address retired');
        loadPool();
      }
      closeAction();
    } catch (e) {
      toast.error(_err(e, 'Action failed'));
    } finally { setActionBusy(false); }
  };

  const addPool = async () => {
    if (!newAddr.trim()) { toast.error('Enter an address'); return; }
    try {
      await api.post('/admin/deposit_addresses', {
        symbol: newSym, network: newNet, address: newAddr.trim(),
        memo_or_tag: newMemo.trim() || null, notes: newNotes.trim() || null,
      });
      toast.success('Address added to pool');
      setNewAddr(''); setNewMemo(''); setNewNotes(''); loadPool();
    } catch (e) { toast.error(_err(e, 'Failed to add')); }
  };
  const assignManual = async () => {
    if (!assignUserId.trim() || !assignAddr.trim()) { toast.error('User ID and address required'); return; }
    try {
      await api.post(`/admin/users/${assignUserId.trim()}/deposit_address`, {
        symbol: assignSym, network: assignNet, address: assignAddr.trim(),
        memo_or_tag: assignMemo.trim() || null,
      });
      toast.success('Assigned');
      setAssignAddr(''); setAssignMemo(''); loadAssignments();
    } catch (e) { toast.error(_err(e, 'Failed to assign')); }
  };
  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

  return (
    <div className="space-y-4" data-testid="admin-ops-panel">
      {/* Sub-tabs */}
      <div className="inline-flex border border-[#2B3139] bg-[#181A20] p-0.5 flex-wrap" data-testid="admin-ops-subtabs">
        {[
          ['pool', 'Address Pool', poolCounts.available ?? '·'],
          ['assignments', 'User Addresses'],
          ['deposits', 'Pending Deposits', depReqs.length || null],
          ['withdrawals', 'Pending Withdrawals', wdReqs.length || null],
          ['kyc', 'KYC Review', kycQueue.length || null],
          ['banners', 'Banners'],
          ['bgn', 'BGN Staking'],
          ['staking_pools', 'Staking Pools'],
          ['listings', 'Listings'],
          ['hd_wallet', 'HD Wallet'],
          ['settings', 'Settings'],
        ].map(([k, label, badge]) => (
          <button key={k} onClick={() => setSub(k)}
            className={`px-3 py-1.5 text-xs uppercase font-mono flex items-center gap-1.5 ${sub === k ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}
            data-testid={`admin-ops-sub-${k}`}>
            {label}
            {badge != null && badge !== '·' && <span className={`text-[9px] px-1.5 rounded-sm ${sub === k ? 'bg-[#0B0E11] text-[#FCD535]' : 'bg-[#F6465D] text-white'}`}>{badge}</span>}
          </button>
        ))}
      </div>

      {/* Address Pool */}
      {sub === 'pool' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="admin-pool-counts">
            <PoolStat label="Total" value={poolCounts.total || 0} color="#B7BDC6" />
            <PoolStat label="Available" value={poolCounts.available || 0} color="#0ECB81" />
            <PoolStat label="Assigned" value={poolCounts.assigned || 0} color="#F0B90B" />
            <PoolStat label="Retired" value={poolCounts.retired || 0} color="#5E6673" />
          </div>

          {/* Add form */}
          <div className="border border-[#2B3139] bg-[#181A20] p-4" data-testid="admin-pool-add-form">
            <div className="text-xs uppercase font-mono text-[#848E9C] mb-2 flex items-center gap-1"><Plus className="w-3 h-3" /> Add address to pool</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <select value={newSym} onChange={e => { setNewSym(e.target.value); setNewNet(NETWORKS[e.target.value][0]); }}
                className="bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm" data-testid="admin-pool-symbol">
                {Object.keys(NETWORKS).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={newNet} onChange={e => setNewNet(e.target.value)}
                className="bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm" data-testid="admin-pool-network">
                {(NETWORKS[newSym] || []).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <input value={newAddr} onChange={e => setNewAddr(e.target.value)} placeholder="Address"
                className="bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm col-span-2" data-testid="admin-pool-address" />
              <input value={newMemo} onChange={e => setNewMemo(e.target.value)} placeholder="Memo/Tag (opt)"
                className="bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm" data-testid="admin-pool-memo" />
              <button onClick={addPool}
                className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold uppercase text-xs px-3 py-1.5 rounded-sm"
                data-testid="admin-pool-add-btn">Add</button>
            </div>
            <input value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Internal notes (opt)"
              className="mt-2 w-full bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm" data-testid="admin-pool-notes" />
          </div>

          {/* Pool table */}
          <div className="border border-[#2B3139] bg-[#181A20] overflow-x-auto" data-testid="admin-pool-table">
            <table className="w-full text-xs">
              <thead className="bg-[#0B0E11] text-[#848E9C] uppercase font-mono">
                <tr>
                  <th className="text-left px-3 py-2">Coin/Net</th>
                  <th className="text-left px-3 py-2">Address</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Assigned</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pool.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-[#5E6673] py-6">
                    {loading ? 'Loading…' : 'No addresses in pool. Add one above.'}
                  </td></tr>
                ) : pool.map(r => (
                  <tr key={r.address_id} className="border-t border-[#2B3139]/60 hover:bg-[#0B0E11]/40" data-testid={`admin-pool-row-${r.address_id}`}>
                    <td className="px-3 py-2 font-mono text-white">{r.symbol}<span className="text-[#848E9C]">/{r.network}</span></td>
                    <td className="px-3 py-2 font-mono text-[#B7BDC6]">
                      <div className="flex items-center gap-1">
                        <span className="max-w-[240px] truncate">{r.address}</span>
                        <button onClick={() => copyText(r.address)} className="text-[#FCD535]"><Copy className="w-3 h-3" /></button>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-sm ${
                        r.status === 'available' ? 'bg-[#0ECB81]/20 text-[#0ECB81]' :
                        r.status === 'assigned' ? 'bg-[#F0B90B]/20 text-[#F0B90B]' : 'bg-[#5E6673]/20 text-[#5E6673]'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-[#B7BDC6]">{r.assigned_email || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {r.status !== 'assigned' && (
                        <button onClick={() => openAction('pool', 'retire', r)} className="text-[#F6465D] hover:underline text-xs uppercase font-mono" data-testid={`admin-pool-retire-${r.address_id}`}>Retire</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Assignments */}
      {sub === 'assignments' && (
        <div className="space-y-4">
          <div className="border border-[#2B3139] bg-[#181A20] p-4" data-testid="admin-assign-form">
            <div className="text-xs uppercase font-mono text-[#848E9C] mb-2 flex items-center gap-1">
              <Users className="w-3 h-3" /> Manually assign address to user
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <input value={assignUserId} onChange={e => setAssignUserId(e.target.value)} placeholder="user_id"
                className="bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm col-span-2" data-testid="admin-assign-user-id" />
              <select value={assignSym} onChange={e => { setAssignSym(e.target.value); setAssignNet(NETWORKS[e.target.value][0]); }}
                className="bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm" data-testid="admin-assign-symbol">
                {Object.keys(NETWORKS).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={assignNet} onChange={e => setAssignNet(e.target.value)}
                className="bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm" data-testid="admin-assign-network">
                {(NETWORKS[assignSym] || []).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <input value={assignAddr} onChange={e => setAssignAddr(e.target.value)} placeholder="Address (fresh or existing)"
                className="bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm" data-testid="admin-assign-address" />
              <button onClick={assignManual}
                className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold uppercase text-xs px-3 py-1.5 rounded-sm" data-testid="admin-assign-btn">Assign</button>
            </div>
            <input value={assignMemo} onChange={e => setAssignMemo(e.target.value)} placeholder="Memo/Tag (optional)"
              className="mt-2 w-full bg-[#0B0E11] border border-[#2B3139] text-white text-xs font-mono px-2 py-1.5 rounded-sm" />
          </div>
          <div className="border border-[#2B3139] bg-[#181A20] overflow-x-auto" data-testid="admin-assign-table">
            <table className="w-full text-xs">
              <thead className="bg-[#0B0E11] text-[#848E9C] uppercase font-mono">
                <tr>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Coin/Net</th>
                  <th className="text-left px-3 py-2">Address</th>
                  <th className="text-left px-3 py-2">Since</th>
                </tr>
              </thead>
              <tbody>
                {assignments.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-[#5E6673] py-6">
                    {loading ? 'Loading…' : 'No assignments yet.'}
                  </td></tr>
                ) : assignments.map(r => (
                  <tr key={`${r.user_id}-${r.symbol}-${r.network}`} className="border-t border-[#2B3139]/60">
                    <td className="px-3 py-2 text-white">{r.email || r.user_id}</td>
                    <td className="px-3 py-2 font-mono">{r.symbol}<span className="text-[#848E9C]">/{r.network}</span></td>
                    <td className="px-3 py-2 font-mono text-[#B7BDC6] max-w-[260px] truncate">{r.address}</td>
                    <td className="px-3 py-2 text-[#5E6673]">{r.assigned_at ? new Date(r.assigned_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Deposits */}
      {sub === 'deposits' && (
        <div className="border border-[#2B3139] bg-[#181A20] overflow-x-auto" data-testid="admin-deposits-table">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#2B3139]">
            <div className="text-xs uppercase font-mono text-[#848E9C] flex items-center gap-1"><ArrowDownToLine className="w-3 h-3" /> {depReqs.length} pending deposits</div>
            <button onClick={loadDeposits} className="text-xs text-[#FCD535] flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[#0B0E11] text-[#848E9C] uppercase font-mono">
              <tr>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Tx Hash</th>
                <th className="text-left px-3 py-2">Address</th>
                <th className="text-left px-3 py-2">Submitted</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {depReqs.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-[#5E6673] py-6">{loading ? 'Loading…' : 'No pending deposits.'}</td></tr>
              ) : depReqs.map(r => (
                <tr key={r.request_id} className={`border-t border-[#2B3139]/60 ${r.risk_flags && r.risk_flags.length ? 'bg-[#F6465D]/5' : ''}`} data-testid={`admin-deposit-row-${r.request_id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span>{r.email || r.user_id}</span>
                      <RiskBadges flags={r.risk_flags} />
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-white">{r.amount} {r.symbol}<span className="text-[#848E9C]">/{r.network}</span></td>
                  <td className="px-3 py-2 font-mono text-[#B7BDC6] max-w-[200px] truncate">{r.tx_hash}</td>
                  <td className="px-3 py-2 font-mono text-[#B7BDC6] max-w-[180px] truncate">{r.address}</td>
                  <td className="px-3 py-2 text-[#5E6673]">{new Date(r.submitted_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => openAction('deposit', 'approve', r)} className="text-[#0ECB81] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-deposit-approve-${r.request_id}`}><Check className="w-3 h-3" /> Approve</button>
                    <button onClick={() => openAction('deposit', 'reject', r)} className="text-[#F6465D] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-deposit-reject-${r.request_id}`}><X className="w-3 h-3" /> Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending Withdrawals */}
      {sub === 'withdrawals' && (
        <div className="space-y-3">
          <AutoApproveSettings />
          <div className="border border-[#2B3139] bg-[#181A20] overflow-x-auto" data-testid="admin-withdrawals-table">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#2B3139]">
            <div className="text-xs uppercase font-mono text-[#848E9C] flex items-center gap-1"><ArrowUpFromLine className="w-3 h-3" /> {wdReqs.length} pending withdrawals</div>
            <button onClick={loadWithdraws} className="text-xs text-[#FCD535] flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[#0B0E11] text-[#848E9C] uppercase font-mono">
              <tr>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Destination</th>
                <th className="text-left px-3 py-2">Submitted</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {wdReqs.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-[#5E6673] py-6">{loading ? 'Loading…' : 'No pending withdrawals.'}</td></tr>
              ) : wdReqs.map(r => (
                <tr key={r.request_id} className={`border-t border-[#2B3139]/60 ${r.risk_flags && r.risk_flags.length ? 'bg-[#F6465D]/5' : ''}`} data-testid={`admin-wd-row-${r.request_id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span>{r.email || r.user_id}</span>
                      <RiskBadges flags={r.risk_flags} />
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-white">{r.amount} {r.symbol}</td>
                  <td className="px-3 py-2 font-mono text-[#B7BDC6] max-w-[240px] truncate">{r.address}</td>
                  <td className="px-3 py-2 text-[#5E6673]">{new Date(r.submitted_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => openAction('withdraw', 'approve', r)} className="text-[#0ECB81] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-wd-approve-${r.request_id}`}><Check className="w-3 h-3" /> Approve</button>
                    <button onClick={() => openAction('withdraw', 'reject', r)} className="text-[#F6465D] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-wd-reject-${r.request_id}`}><X className="w-3 h-3" /> Reject & Refund</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* KYC Review */}
      {sub === 'kyc' && (
        <div className="border border-[#2B3139] bg-[#181A20] overflow-x-auto" data-testid="admin-kyc-table">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#2B3139]">
            <div className="text-xs uppercase font-mono text-[#848E9C] flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {kycQueue.length} pending KYC</div>
            <button onClick={loadKyc} className="text-xs text-[#FCD535] flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[#0B0E11] text-[#848E9C] uppercase font-mono">
              <tr>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Full Name</th>
                <th className="text-left px-3 py-2">Country</th>
                <th className="text-left px-3 py-2">Doc</th>
                <th className="text-left px-3 py-2">Submitted</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {kycQueue.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-[#5E6673] py-6">{loading ? 'Loading…' : 'No pending KYC submissions.'}</td></tr>
              ) : kycQueue.map(r => (
                <tr key={r.kyc_id} className="border-t border-[#2B3139]/60" data-testid={`admin-kyc-row-${r.kyc_id}`}>
                  <td className="px-3 py-2">{r.email || r.user_id}</td>
                  <td className="px-3 py-2 text-white">{r.full_name || r.name || '—'}</td>
                  <td className="px-3 py-2 text-[#B7BDC6]">{r.country || '—'}</td>
                  <td className="px-3 py-2 text-[#B7BDC6]">{r.doc_type || '—'}</td>
                  <td className="px-3 py-2 text-[#5E6673]">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => openAction('kyc', 'approve', r)} className="text-[#0ECB81] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-kyc-approve-${r.kyc_id}`}><Check className="w-3 h-3" /> Approve</button>
                    <button onClick={() => openAction('kyc', 'reject', r)} className="text-[#F6465D] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-kyc-reject-${r.kyc_id}`}><X className="w-3 h-3" /> Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Banners CRUD */}
      {sub === 'banners' && <BannersPanel />}

      {/* BGN Staking admin */}
      {sub === 'bgn' && <BgnStakingAdminPanel />}

      {/* Staking Pools admin (regular pools) */}
      {sub === 'staking_pools' && <StakingPoolsAdminPanel />}

      {/* Coin/Token Listing Applications */}
      {sub === 'listings' && <ListingsAdminPanel />}

      {/* HD Wallet Infrastructure */}
      {sub === 'hd_wallet' && <HDWalletAdminPanel />}

      {/* Settings — admin-editable global config */}
      {sub === 'settings' && (
        <div className="space-y-4" data-testid="admin-settings-panel">
          {/* Tier 1 cap (non-KYC) */}
          <div className="border border-[#2B3139] bg-[#181A20] p-5" data-testid="admin-kyc-threshold-card">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-[#F0B90B] flex-shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-white font-semibold text-base">Tier 0 → Tier 1 · Non-KYC cap (USD, lifetime)</div>
                <div className="text-xs text-[#B7BDC6] mt-1">
                  Users without KYC can withdraw up to this cumulative USD amount. Beyond it, they must complete basic KYC (name + document + selfie).
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-[#848E9C] text-sm font-mono">USD $</span>
                  <input
                    type="number"
                    value={kycThresholdDraft}
                    onChange={e => setKycThresholdDraft(e.target.value)}
                    placeholder="50000"
                    className="bg-[#0B0E11] border border-[#2B3139] text-white text-sm font-mono px-3 py-2 rounded-sm w-40 focus:border-[#FCD535] outline-none"
                    data-testid="admin-kyc-threshold-input"
                  />
                  <button
                    onClick={saveKycThreshold}
                    disabled={kycSaving || String(kycThreshold) === String(kycThresholdDraft)}
                    className="bg-[#FCD535] hover:bg-[#F0B90B] disabled:opacity-40 text-[#0B0E11] font-semibold uppercase text-xs px-4 py-2 rounded-sm"
                    data-testid="admin-kyc-threshold-save"
                  >
                    {kycSaving ? 'Saving…' : 'Save'}
                  </button>
                  <div className="flex gap-1 ml-2" data-testid="admin-kyc-threshold-quick">
                    {[10000, 25000, 50000, 100000, 250000].map(v => (
                      <button key={v} onClick={() => setKycThresholdDraft(String(v))}
                        className={`text-[10px] uppercase font-mono px-2 py-1 border rounded-sm ${
                          String(v) === kycThresholdDraft
                            ? 'bg-[#FCD535] text-[#0B0E11] border-[#FCD535]'
                            : 'border-[#2B3139] text-[#848E9C] hover:border-[#FCD535]'
                        }`}>${v >= 1000 ? `${v / 1000}k` : v}</button>
                    ))}
                  </div>
                </div>
                <div className="mt-3 text-[11px] font-mono text-[#5E6673]">
                  Effective Tier 0 cap: <span className="text-[#F0B90B]">${kycThreshold.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tier 2 cap (post-basic-KYC) */}
          <div className="border border-[#2B3139] bg-[#181A20] p-5" data-testid="admin-tier2-threshold-card">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-[#0ECB81] flex-shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-white font-semibold text-base">Tier 1 → Tier 2 · Basic-KYC cap (USD, lifetime)</div>
                <div className="text-xs text-[#B7BDC6] mt-1">
                  Users with basic KYC (Tier 1) can withdraw up to this cumulative USD amount. Beyond it, they must upgrade to Tier 2 by submitting proof-of-address. Tier 2 users have <strong className="text-[#0ECB81]">no cap</strong>.
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-[#848E9C] text-sm font-mono">USD $</span>
                  <input
                    type="number"
                    value={tier2ThresholdDraft}
                    onChange={e => setTier2ThresholdDraft(e.target.value)}
                    placeholder="500000"
                    className="bg-[#0B0E11] border border-[#2B3139] text-white text-sm font-mono px-3 py-2 rounded-sm w-40 focus:border-[#0ECB81] outline-none"
                    data-testid="admin-tier2-threshold-input"
                  />
                  <button
                    onClick={saveTier2Threshold}
                    disabled={kycSaving || String(tier2Threshold) === String(tier2ThresholdDraft)}
                    className="bg-[#0ECB81] hover:bg-[#0AB673] disabled:opacity-40 text-[#0B0E11] font-semibold uppercase text-xs px-4 py-2 rounded-sm"
                    data-testid="admin-tier2-threshold-save"
                  >
                    {kycSaving ? 'Saving…' : 'Save'}
                  </button>
                  <div className="flex gap-1 ml-2" data-testid="admin-tier2-threshold-quick">
                    {[100000, 250000, 500000, 1000000, 5000000].map(v => (
                      <button key={v} onClick={() => setTier2ThresholdDraft(String(v))}
                        className={`text-[10px] uppercase font-mono px-2 py-1 border rounded-sm ${
                          String(v) === tier2ThresholdDraft
                            ? 'bg-[#0ECB81] text-[#0B0E11] border-[#0ECB81]'
                            : 'border-[#2B3139] text-[#848E9C] hover:border-[#0ECB81]'
                        }`}>${v >= 1000000 ? `${v / 1000000}M` : `${v / 1000}k`}</button>
                    ))}
                  </div>
                </div>
                <div className="mt-3 text-[11px] font-mono text-[#5E6673]">
                  Effective Tier 1 cap: <span className="text-[#0ECB81]">${tier2Threshold.toLocaleString()}</span>
                  <span className="mx-2 text-[#2B3139]">·</span>
                  Tier 2 cap: <span className="text-[#0ECB81]">unlimited</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Universal action modal */}
      <ActionModal
        action={action}
        field1={actionField1} setField1={setActionField1}
        field2={actionField2} setField2={setActionField2}
        busy={actionBusy}
        onClose={closeAction}
        onConfirm={runAction}
      />
    </div>
  );
}

function PoolStat({ label, value, color }) {
  return (
    <div className="border border-[#2B3139] bg-[#181A20] p-2">
      <div className="text-[10px] uppercase font-mono text-[#848E9C]">{label}</div>
      <div className="font-mono text-lg" style={{ color }}>{value}</div>
    </div>
  );
}

// Auto-computed risk flags on pending deposits/withdrawals. Renders a compact
// red "AT RISK" pill with a tooltip listing every flag reason.
function RiskBadges({ flags }) {
  if (!flags || !flags.length) return null;
  const title = flags.map(f => `• ${f.label}`).join('\n');
  return (
    <span
      title={title}
      className="ml-2 inline-flex items-center gap-1 text-[9px] uppercase font-mono px-1.5 py-0.5 rounded-sm bg-[#F6465D]/20 text-[#F6465D] border border-[#F6465D]/40 whitespace-nowrap"
      data-testid="admin-risk-badge"
    >
      <Flame className="w-3 h-3" /> {flags.length === 1 ? flags[0].label : `${flags.length} risk flags`}
    </span>
  );
}

// Parse an axios-style error into a display string. Handles both string and
// object detail bodies from FastAPI HTTPException.
function _err(e, fallback) {
  if (e?.message && !e?.response) return e.message;
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (d.message) return d.message;
  try { return JSON.stringify(d); } catch { return fallback; }
}

// Single modal component used for every approve/reject/retire flow.
// Replaces the previous window.prompt / window.confirm calls which were
// silently blocked in some browsers.
function ActionModal({ action, field1, setField1, field2, setField2, busy, onClose, onConfirm }) {
  if (!action) return null;
  const { kind, mode, row } = action;
  const config = _actionConfig(kind, mode, row);
  return (
    <Dialog open={!!action} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md" data-testid="admin-action-modal">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${config.danger ? 'text-[#F6465D]' : 'text-[#FCD535]'}`}>
            {config.danger ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            {config.title}
          </DialogTitle>
        </DialogHeader>

        {/* Row summary */}
        {row && (
          <div className="border border-[#2B3139] bg-[#0B0E11] p-3 space-y-1 text-xs" data-testid="admin-action-summary">
            {config.summary(row)}
          </div>
        )}

        {/* Field 1 */}
        {config.field1 && (
          <div>
            <label className="text-[10px] uppercase font-mono text-[#848E9C] mb-1 block">{config.field1.label}</label>
            <Input value={field1} onChange={e => setField1(e.target.value)} placeholder={config.field1.placeholder}
              type={config.field1.type || 'text'}
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono"
              data-testid="admin-action-field-1" autoFocus />
          </div>
        )}
        {/* Field 2 (optional) */}
        {config.field2 && (
          <div>
            <label className="text-[10px] uppercase font-mono text-[#848E9C] mb-1 block">{config.field2.label}</label>
            <Input value={field2} onChange={e => setField2(e.target.value)} placeholder={config.field2.placeholder}
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono"
              data-testid="admin-action-field-2" />
          </div>
        )}

        {config.warning && (
          <div className={`text-[11px] p-2 rounded-sm ${config.danger ? 'border border-[#F6465D]/40 bg-[#F6465D]/10 text-[#F6465D]' : 'border border-[#F0B90B]/40 bg-[#F0B90B]/10 text-[#F0B90B]'}`}>
            {config.warning}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}
            className="bg-[#0B0E11] border border-[#2B3139] text-[#B7BDC6] hover:bg-[#2B3139] rounded-sm"
            data-testid="admin-action-cancel">
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={busy}
            className={`rounded-sm font-semibold ${config.danger ? 'bg-[#F6465D] hover:bg-[#E33A50] text-white' : 'bg-[#0ECB81] hover:bg-[#0AB673] text-[#0B0E11]'}`}
            data-testid="admin-action-confirm">
            {busy ? 'Working…' : config.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Config for each (kind, mode) — title, danger tone, fields, summary render.
function _actionConfig(kind, mode, row) {
  const cfg = {
    'deposit.approve': {
      title: `Approve deposit ${row?.symbol}/${row?.network}`,
      confirmLabel: 'Credit balance',
      summary: r => (<>
        <div><span className="text-[#848E9C]">User:</span> <span className="text-white">{r.email || r.user_id}</span></div>
        <div><span className="text-[#848E9C]">Amount claimed:</span> <span className="text-white font-mono">{r.amount} {r.symbol}</span></div>
        <div><span className="text-[#848E9C]">Tx hash:</span> <span className="text-white font-mono break-all">{r.tx_hash}</span></div>
        <div><span className="text-[#848E9C]">To address:</span> <span className="text-white font-mono break-all">{r.address}</span></div>
      </>),
      field1: { label: `Credit amount (${row?.symbol})`, placeholder: 'e.g. 100', type: 'number' },
      field2: { label: 'Admin note (optional)', placeholder: 'Confirmed on-chain' },
    },
    'deposit.reject': {
      title: `Reject deposit ${row?.symbol}/${row?.network}`,
      confirmLabel: 'Reject deposit',
      danger: true,
      summary: r => (<>
        <div><span className="text-[#848E9C]">User:</span> <span className="text-white">{r.email || r.user_id}</span></div>
        <div><span className="text-[#848E9C]">Claim:</span> <span className="text-white font-mono">{r.amount} {r.symbol}</span></div>
        <div><span className="text-[#848E9C]">Tx hash:</span> <span className="text-white font-mono break-all">{r.tx_hash}</span></div>
      </>),
      field1: { label: 'Reason (shown to user in email + notification)', placeholder: 'Tx not found on-chain' },
      warning: 'The user will be notified. No balance will be credited.',
    },
    'withdraw.approve': {
      title: `Approve withdrawal`,
      confirmLabel: 'Approve & broadcast',
      summary: r => (<>
        <div><span className="text-[#848E9C]">User:</span> <span className="text-white">{r.email || r.user_id}</span></div>
        <div><span className="text-[#848E9C]">Amount:</span> <span className="text-white font-mono">{r.amount} {r.symbol}</span></div>
        <div><span className="text-[#848E9C]">Destination:</span> <span className="text-white font-mono break-all">{r.address}</span></div>
      </>),
      field1: { label: 'Tx hash (optional — auto-generated if blank)', placeholder: '0x… (leave blank to auto)' },
      field2: { label: 'Admin note (optional)', placeholder: 'Verified — cleared' },
      warning: 'Balance was already debited when the user submitted. Approving broadcasts the tx.',
    },
    'withdraw.reject': {
      title: `Reject & refund withdrawal`,
      confirmLabel: 'Reject & refund',
      danger: true,
      summary: r => (<>
        <div><span className="text-[#848E9C]">User:</span> <span className="text-white">{r.email || r.user_id}</span></div>
        <div><span className="text-[#848E9C]">Amount:</span> <span className="text-white font-mono">{r.amount} {r.symbol}</span></div>
        <div><span className="text-[#848E9C]">Destination:</span> <span className="text-white font-mono break-all">{r.address}</span></div>
      </>),
      field1: { label: 'Reason (shown to user)', placeholder: 'Suspicious activity' },
      warning: `Balance of ${row?.amount} ${row?.symbol} will be refunded automatically.`,
    },
    'kyc.approve': {
      title: 'Approve KYC submission',
      confirmLabel: 'Approve KYC',
      summary: r => (<>
        <div><span className="text-[#848E9C]">User:</span> <span className="text-white">{r.email || r.user_id}</span></div>
        <div><span className="text-[#848E9C]">Name:</span> <span className="text-white">{r.full_name || r.name || '—'}</span></div>
        <div><span className="text-[#848E9C]">Country:</span> <span className="text-white">{r.country || '—'}</span></div>
      </>),
      warning: 'User will be marked KYC-verified and can withdraw once 2FA is enabled.',
    },
    'kyc.reject': {
      title: 'Reject KYC submission',
      confirmLabel: 'Reject KYC',
      danger: true,
      summary: r => (<>
        <div><span className="text-[#848E9C]">User:</span> <span className="text-white">{r.email || r.user_id}</span></div>
        <div><span className="text-[#848E9C]">Name:</span> <span className="text-white">{r.full_name || r.name || '—'}</span></div>
      </>),
      field1: { label: 'Reason', placeholder: 'Documents unclear — please resubmit' },
    },
    'pool.retire': {
      title: 'Retire this address?',
      confirmLabel: 'Retire address',
      danger: true,
      summary: r => (<>
        <div><span className="text-[#848E9C]">Coin/Net:</span> <span className="text-white font-mono">{r.symbol}/{r.network}</span></div>
        <div><span className="text-[#848E9C]">Address:</span> <span className="text-white font-mono break-all">{r.address}</span></div>
      </>),
      warning: 'The address will be marked retired and never reassigned. Existing user assignments to this address remain unchanged.',
    },
  };
  return cfg[`${kind}.${mode}`] || { title: 'Action', confirmLabel: 'Confirm', summary: () => null };
}

// Auto-approval threshold — sits above the Withdrawals queue. Any user
// withdrawal <= this USD amount skips manual review and is broadcast immediately.
// Set to 0 to disable (all withdrawals require manual review).
function AutoApproveSettings() {
  const [threshold, setThreshold] = useState('');
  const [saved, setSaved] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/admin/settings/withdrawals');
      const v = Number(r.data?.auto_approve_threshold_usd || 0);
      setThreshold(String(v));
      setSaved(v);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const v = parseFloat(threshold);
    if (isNaN(v) || v < 0) { toast.error('Enter a valid number (0 or more)'); return; }
    setBusy(true);
    try {
      const r = await api.put('/admin/settings/withdrawals', { auto_approve_threshold_usd: v });
      setSaved(Number(r.data?.auto_approve_threshold_usd || 0));
      toast.success(v > 0
        ? `Auto-approve threshold set to $${v.toLocaleString()}`
        : 'Auto-approval disabled — all withdrawals require manual review');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#F0B90B]/40 bg-[#F0B90B]/5 p-3 rounded-sm" data-testid="admin-auto-approve-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase font-mono text-[#F0B90B] font-bold mb-0.5">Auto-approve threshold</div>
          <div className="text-[11px] text-[#B7BDC6]">
            Withdrawals under this USD value are auto-approved. Set to <span className="font-mono text-white">0</span> to force manual review for everything.
            {saved > 0 && <> Currently: <span className="font-mono text-[#0ECB81]">${saved.toLocaleString()}</span> auto-approved instantly.</>}
            {saved === 0 && <> Currently: <span className="font-mono text-[#F6465D]">Disabled</span> — all withdrawals go to manual queue.</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#848E9C] text-sm">$</span>
            <Input value={threshold} onChange={e => setThreshold(e.target.value)} type="number" min="0" step="10"
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono h-9 w-32 pl-5 focus-visible:border-[#F0B90B]"
              data-testid="auto-approve-threshold-input" />
          </div>
          <Button onClick={save} disabled={busy}
            className="bg-[#F0B90B] hover:bg-[#FCD535] text-[#0B0E11] rounded-sm text-xs font-semibold h-9 px-4"
            data-testid="auto-approve-save-btn">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
